import {OverlayModule} from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  Signal,
  signal,
  untracked,
  viewChildren,
} from '@angular/core';
import {outputFromObservable, toObservable} from '@angular/core/rxjs-interop';
import {FormsModule} from '@angular/forms';
import moment, {Moment, unitOfTime} from 'moment';
import {debounceTime, distinctUntilChanged, filter, skip} from 'rxjs/operators';
import {DateFormatService} from '../date-format/date-format.service';
import {DatePickerComponent} from '../date-picker/date-picker.component';
import {MatSymbol} from '../models/mat-symbol';
import {SelectOption} from '../dropdown/select-dropdown/select-dropdown';
import {SelectDropdownComponent} from '../dropdown/select-dropdown/select-dropdown.component';
import {
  OpenPanel,
  Period,
  PeriodGranularity,
  PeriodMode,
  PeriodTimelineMode,
  RecentUnit,
  TextField,
} from './period-selector9';
import {applyTime, buildPeriodParams, computeEmitsTime, computeUsesTimeframe} from './period-params';
import {
  DATE_INPUT_FORMATS,
  DATE_PICKER_POSITIONS,
  DEFAULT_END_TIME,
  DEFAULT_START_TIME,
  MODE_LABELS,
  RECENT_UNIT_LABELS,
  TIME_FORMAT,
  US_DATE_INPUT_FORMATS,
} from './period-selector9.constants';
import {nowInTimezone} from '../timezone';
import {OverlayPanelDirective} from '../overlay/overlay-panel.directive';
import {TimePickerComponent} from '../time-picker/time-picker.component';
import {formatTime} from '../time-picker/time-parse';

/**
 * Value equality for {@link Period}. `startDate`/`endDate` are fresh clones every time
 * `period()` recomputes, so a plain `===`/`isSame` chain on possibly-null Moments is easy to
 * get wrong — `.valueOf()` collapses "same instant" and "both null" to the same comparable.
 */
function periodsEqual(a: Period, b: Period): boolean {
  return a.mode === b.mode
    && a.timelineMode === b.timelineMode
    && (a.startDate?.valueOf() ?? null) === (b.startDate?.valueOf() ?? null)
    && (a.endDate?.valueOf() ?? null) === (b.endDate?.valueOf() ?? null)
    && a.startTime === b.startTime
    && a.endTime === b.endTime
    && a.excludeWeekends === b.excludeWeekends;
}

/**
 * Period selector, 9th generation. Compact `date → date` UI so that the timeline/schedule
 * controls fit next to it on a toolbar.
 *
 * Three independent settings shape it:
 * - `granularity`: `datetime` gives the range endpoints a time of day, and every emitted
 *   period then carries one. Until the user fills a time in it stays greyed out, to signal
 *   that the whole first (`00:00-`) and last (`-23:59`) day are counted.
 * - `hasTimeframe`: adds the Hours panel — a `startTime`/`endTime` timeframe ("időablak")
 *   repeating on every day of the range. It takes over the time of day, so the dates go out
 *   whole however `granularity` is set; the two would otherwise collide.
 * - `hasTimelineSwitch`: offers Timeline (one uninterrupted period) against Schedule (the
 *   timeframe, plus a weekend toggle). Schedule implies a timeframe; `hasTimeframe` on its own
 *   is the Hours panel without the switch or the weekend toggle. A switchable selector shares
 *   one timeframe between the two sides, so flipping keeps whatever was set.
 *
 * Emits a {@link Period} whose `params` already match the legacy selector's backend
 * contract, so consumers can feed them straight into `additionalQueryParams`.
 *
 * Display follows the company's default/US profile (`DateFormatService`): US dates read `Sep 16`
 * inside the current year and `Dec 31, 2025` outside it, times are 12h. What is emitted does not
 * change with the profile.
 */
@Component({
  selector: 'app-period-selector9',
  imports: [FormsModule, OverlayModule, OverlayPanelDirective, DatePickerComponent, SelectDropdownComponent, TimePickerComponent],
  templateUrl: './period-selector9.component.html',
  styleUrl: './period-selector9.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {class: 'app-period-selector9'},
})
export class PeriodSelector9Component {
  /** `datetime` swaps in the calendar_clock icon and the per-endpoint time inputs. */
  readonly granularity = input<PeriodGranularity>('date');
  /** Modes offered in the leading dropdown, in display order. */
  readonly modes = input<readonly PeriodMode[]>(['all', 'day', 'range', 'recent']);
  /** Overrides the dropdown title of individual modes, e.g. `{day: 'End'}`. */
  readonly modeLabels = input<Partial<Record<PeriodMode, string>>>({});
  /** Shows the Timeline/Schedule switch. Without it the component is always `timeline`. */
  readonly hasTimelineSwitch = input(false);
  /** Adds the Hours panel: a timeframe ("időablak") repeating on every day of the range. */
  readonly hasTimeframe = input(false);
  readonly recentUnits = input<readonly RecentUnit[]>(['hours', 'days', 'weeks', 'months']);
  readonly minRecent = input(1);
  readonly maxRecent = input(999);
  /** Bounds for the calendars, the day stepper and typed dates alike. Unbounded by default. */
  readonly minDate = input<Moment | null>(null);
  readonly maxDate = input<Moment | null>(null);
  /**
   * IANA zone whose wall clock "now"/"today" is derived on: the default dates, recent-mode
   * windows and the calendars' today ring. `null` (the default) is the selected company's zone —
   * the one the backend reads dates in, which can be on another calendar day than the browser
   * (a US company used from Hungary) — and the browser clock only when there is no single one.
   */
  readonly timezone = input<string | null>(null);
  /** Off by default: the date fields are pick-only (the calendar). Set true to also allow typing
   *  a date into them. The time/timeframe fields stay typable regardless. */
  readonly allowTyping = input(false);
  /** Read-only display: greys the control and blocks all interaction and focus (via `inert`), while
   *  still reflecting its bound value. For a selector driven programmatically rather than by the
   *  user — not `disabled`, whose value would be frozen. */
  readonly readOnly = input(false);
  /**
   * Read-only text before the date in `day` mode, in dark grey: where a period ending on the day
   * starts, when only the data knows (the utilization History chart's shift bars, `Aug 28 to`).
   * Changes nothing about what is picked or emitted.
   */
  readonly dayPrefix = input<string | null>(null);
  /**
   * The leading mode pill: the dropdown, or the static label of a single mode. Off where the context
   * already says what the date is (the History chart's shift mode, whose `dayPrefix` reads `… to`).
   */
  readonly hasModePill = input(true);

  readonly mode = model<PeriodMode>('range');
  readonly timelineMode = model<PeriodTimelineMode>('timeline');
  readonly startDate = model<Moment>(moment().startOf('day').subtract(1, 'week'));
  /** Also holds the selected day in `day` mode, where it defaults to today. */
  readonly endDate = model<Moment>(moment().endOf('day'));
  readonly recentValue = model(2);
  readonly recentUnit = model<RecentUnit>('weeks');
  /**
   * The Sat/Sun toggle: whether weekends are part of the timeframe. Kept as the inclusive
   * state because that is what the checkbox shows; the request inverts it to `excludeWeekends`.
   */
  readonly weekends = model(true);
  /**
   * The timeframe, `HH:mm` — also the endpoint times on a timeline that can switch. `null`
   * means unset: the field shows the default greyed, and an unset endpoint resolves to it.
   */
  readonly startTime = model<string | null>(null);
  readonly endTime = model<string | null>(null);

  /** Set once the user picks a time explicitly — until then the endpoint reads as a whole day. */
  protected readonly startTimeSet = signal(false);
  protected readonly endTimeSet = signal(false);

  protected readonly openPanel = signal<OpenPanel>(null);

  private readonly formats = inject(DateFormatService);
  /** {@link timezone}, else the selected company's; `null` only while there is neither. */
  protected readonly effectiveTimezone = computed(() => this.timezone() ?? this.formats.timezone());
  /** The time fields' format — what their placeholders are written in, and how wide they are. */
  protected readonly hourFormat = computed(() => this.formats.is12h() ? '12h' : '24h');
  /** The US format spells the month out, so a phone keyboard needs letters there. */
  protected readonly dateInputMode = computed(() => this.formats.profile() === 'us' ? 'text' : 'numeric');
  protected readonly datePickerPositions = DATE_PICKER_POSITIONS;

  /** Falls back to the first offered mode when `mode` is not one the consumer allows. */
  protected readonly effectiveMode = computed<PeriodMode>(() => {
    const mode = this.mode();
    const modes = this.modes();
    return modes.includes(mode) ? mode : modes[0] ?? mode;
  });
  protected readonly effectiveTimelineMode = computed<PeriodTimelineMode>(() =>
    this.hasTimelineSwitch() ? this.timelineMode() : 'timeline');
  /**
   * The Schedule side of the switch is selected — which also means the switch exists, since
   * `effectiveTimelineMode` is pinned to `timeline` without it. This is what gates the weekend
   * toggle: weekends only make sense for a timeframe the user can see themselves choosing.
   */
  protected readonly isScheduleMode = computed(() => this.effectiveTimelineMode() === 'schedule');
  /** The timeframe is in play: either the Schedule side, or `hasTimeframe` on its own. */
  protected readonly usesTimeframe = computed(() =>
    computeUsesTimeframe(this.hasTimelineSwitch(), this.isScheduleMode(), this.hasTimeframe()));

  /** Endpoint times only exist without a timeframe — otherwise the timeframe owns the time. */
  protected readonly showTime = computed(() =>
    this.granularity() === 'datetime' && !this.usesTimeframe() && this.effectiveMode() === 'range');
  /**
   * Whether the emitted endpoints carry a time of day. A datetime selector always sends one —
   * including in `recent` mode, where the window ends now and no time is typeable — unless the
   * timeframe owns the time instead, which sends whole dates plus `startTime`/`endTime`.
   *
   * Deliberately independent of `mode`: a `datetime` selector in `day` mode would therefore emit
   * `T00:00`/`T23:59` endpoints rather than bare dates. No consumer pairs `datetime` with `day`
   * today, so this is only a note for whoever first does.
   */
  private readonly emitsTime = computed(() => computeEmitsTime(this.granularity() === 'datetime', this.usesTimeframe()));

  /**
   * Hours are only on offer when the selector deals in times of day: not for a date-only
   * selector, where "the last 6 hours" could not be expressed by the endpoints it emits; and
   * not once a timeframe is active (the Schedule tab, or `hasTimeframe` on its own), since that
   * forces the dates onto whole days regardless — a sub-day "recent" window has nothing to
   * attach to there either.
   */
  protected readonly effectiveRecentUnits = computed<readonly RecentUnit[]>(() => {
    const units = this.recentUnits();
    const allowsHours = this.granularity() === 'datetime' && !this.usesTimeframe();
    return allowsHours ? units : units.filter(unit => unit !== 'hours');
  });
  /** Falls back to the first offered unit when `recentUnit` is not one that is on offer. */
  protected readonly effectiveRecentUnit = computed<RecentUnit>(() => {
    const units = this.effectiveRecentUnits();
    const unit = this.recentUnit();
    return units.includes(unit) ? unit : units[0] ?? unit;
  });

  protected readonly modeOptions = computed(() =>
    this.modes().map(id => ({id, label: this.labelFor(id)})));
  /** `modeOptions` shaped for the shared select dropdown, and its leading calendar symbol. */
  protected readonly modeSelectOptions = computed<SelectOption<PeriodMode>[]>(() =>
    this.modeOptions().map(option => ({value: option.id, label: option.label})));
  protected readonly modeSymbol = computed<MatSymbol>(() =>
    ({symbol: this.showTime() ? 'calendar_clock' : 'calendar_today', filled: true}));
  protected readonly recentUnitOptions = computed(() =>
    this.effectiveRecentUnits().map(id => ({id, label: RECENT_UNIT_LABELS[id]})));
  /** `recentUnitOptions` shaped for the shared select dropdown. */
  protected readonly recentUnitSelectOptions = computed<SelectOption<RecentUnit>[]>(() =>
    this.recentUnitOptions().map(option => ({value: option.id, label: option.label})));
  protected readonly modeLabel = computed(() => this.labelFor(this.effectiveMode()));
  protected readonly recentUnitLabel = computed(() => RECENT_UNIT_LABELS[this.effectiveRecentUnit()]);
  /** A period of zero units means nothing, so 1 is the floor whatever the consumer passes. */
  protected readonly effectiveMinRecent = computed(() => Math.max(1, Math.round(this.minRecent())));

  /**
   * The endpoints of the selected period. `day` mode keeps its selection in `endDate` — it is
   * the "as of" date the mock labels End — so both endpoints are derived from it there. Without
   * that, a mode arriving as an input would emit whatever stale range the two models held.
   *
   * The shared timeframe only overlays `range` mode's endpoints, because that is the one place
   * it is surfaced on the Timeline side (the per-endpoint Start/End time fields). Applying it
   * unconditionally used to corrupt `recent` mode: its dates are computed relative to *now* by
   * `syncRecentDates()`, and forcing them onto `08:00`/`20:00` turned "the last 2 hours" into
   * whatever the shared timeframe happened to hold, discarding the actual relative window.
   */
  private readonly effectiveStartDate = computed(() => {
    if (this.effectiveMode() === 'day') return this.endDate().clone().startOf('day');
    if (this.effectiveMode() === 'range' && this.sharedTimeframe()) {
      return applyTime(this.startDate(), this.resolvedStartTime());
    }
    return this.startDate();
  });
  private readonly effectiveEndDate = computed(() => {
    if (this.effectiveMode() === 'day') return this.endDate().clone().endOf('day');
    if (this.effectiveMode() === 'range' && this.sharedTimeframe()) {
      return applyTime(this.endDate(), this.resolvedEndTime());
    }
    return this.endDate();
  });

  /**
   * A selector carrying the timeline/schedule switch keeps a single timeframe: the timeline
   * shows it on the endpoints, the schedule as the Hours panel. Sharing the state is what lets
   * a time survive flipping between the two.
   */
  private readonly sharedTimeframe = computed(() => this.hasTimelineSwitch());
  /** An unset timeframe endpoint stands in for its default, both on screen and in the request. */
  private readonly resolvedStartTime = computed(() => this.startTime() ?? DEFAULT_START_TIME);
  private readonly resolvedEndTime = computed(() => this.endTime() ?? DEFAULT_END_TIME);
  /**
   * A datetime endpoint's time means something once it is either explicitly set by the user, or
   * reads as something other than its default — which covers a real time the *consumer* supplied,
   * not just one typed through the UI. Anything else is the placeholder `00:00`/`23:59`.
   *
   * Compared as the `HH:mm` the field itself shows, not against the day's boundary instant: a
   * consumer that builds its end from `23:59` rather than `endOf('day')` is a second off it, and
   * an exact comparison read that as a deliberate choice and printed it in black.
   */
  private readonly hasStartTime = computed(() =>
    this.startTimeSet() || this.startDate().format(TIME_FORMAT) !== DEFAULT_START_TIME);
  private readonly hasEndTime = computed(() =>
    this.endTimeSet() || this.endDate().format(TIME_FORMAT) !== DEFAULT_END_TIME);

  /**
   * What each time picker holds, `null` while it means nothing yet — the picker then shows the
   * greyed default as its placeholder. The timeframe pair holds the timeframe; the datetime
   * endpoints hold it too on a switchable selector, and their own date's time otherwise.
   */
  protected readonly startEndpointTime = computed(() => this.sharedTimeframe()
    ? this.startTime()
    : this.hasStartTime() ? this.startDate().format(TIME_FORMAT) : null);
  protected readonly endEndpointTime = computed(() => this.sharedTimeframe()
    ? this.endTime()
    : this.hasEndTime() ? this.endDate().format(TIME_FORMAT) : null);
  /** The unset times' placeholders, in the field's own format: `00:00`, or `12:00 am`. */
  protected readonly startTimePlaceholder = computed(() => formatTime(DEFAULT_START_TIME, this.hourFormat()));
  protected readonly endTimePlaceholder = computed(() => formatTime(DEFAULT_END_TIME, this.hourFormat()));

  private readonly startDateDisplay = computed(() => this.formats.formatPeriodDate(this.startDate()));
  private readonly endDateDisplay = computed(() => this.formats.formatPeriodDate(this.endDate()));
  /**
   * A date field is as wide as its committed text, in `ch`: `2026.09.16` keeps the 10ch it always
   * had, while a US `Sep 16` stays narrow rather than reserving room for `Dec 31, 2025`. It follows
   * the committed value only, so the field never resizes under the cursor.
   */
  protected readonly startDateWidth = computed(() => this.startDateDisplay().length);
  protected readonly endDateWidth = computed(() => this.endDateDisplay().length);

  protected readonly startDateField = this.textField(
    this.startDateDisplay, text => this.parseDate(text), date => this.applyStartDate(date));
  protected readonly endDateField = this.textField(
    this.endDateDisplay, text => this.parseDate(text), date => this.applyEndDate(date));
  protected readonly dayField = this.textField(
    this.endDateDisplay, text => this.parseDate(text), date => this.setDay(date));

  /** Every date field, so validity is checked without re-listing them by hand. */
  private readonly dateFields = [this.startDateField, this.endDateField, this.dayField];
  /** The time fields validate themselves; their `invalid` joins the date fields' below. */
  private readonly timePickers = viewChildren(TimePickerComponent);

  /** Only bounded when the consumer sets `maxDate`; a day in the future is allowed otherwise. */
  protected readonly nextDayDisabled = computed(() => {
    const max = this.maxDate();
    return !!max && !this.endDate().isBefore(max, 'day');
  });
  protected readonly prevDayDisabled = computed(() => {
    const min = this.minDate();
    return !!min && !this.endDate().isAfter(min, 'day');
  });

  /**
   * A structural problem with the committed selection — start after end, an empty timeframe.
   * Reads only the model, never a field draft, so it is what gates emission: typing an
   * in-progress value must not re-emit the old one just because the draft momentarily parses.
   */
  private readonly periodInvalid = computed(() => {
    if (this.effectiveMode() === 'all') return false;
    if (!this.effectiveStartDate().isValid() || !this.effectiveEndDate().isValid()) return true;
    if (this.effectiveStartDate().isAfter(this.effectiveEndDate())) return true;
    return this.usesTimeframe() && this.resolvedStartTime() >= this.resolvedEndTime();
  });

  /** The error fill: the above, plus a draft that does not currently parse. Feedback only. */
  protected readonly invalid = computed(() => this.periodInvalid()
    || this.dateFields.some(field => field.invalid())
    || this.timePickers().some(picker => picker.invalid()));

  private readonly period = computed<Period>(() => {
    const mode = this.effectiveMode();
    const timelineMode = this.effectiveTimelineMode();
    // A timeframe, with or without the switch, means whole dates plus the repeating window.
    const timeframe = this.usesTimeframe();
    // The endpoints become whole days; the timeframe does the narrowing within each of them.
    const startDate = mode === 'all' ? null
      : timeframe ? this.effectiveStartDate().clone().startOf('day') : this.effectiveStartDate().clone();
    const endDate = mode === 'all' ? null
      : timeframe ? this.effectiveEndDate().clone().endOf('day') : this.effectiveEndDate().clone();

    const excludeWeekends = this.isScheduleMode() && !this.weekends();
    const params = buildPeriodParams({
      // Reported only when the switch exists: `effectiveTimelineMode` is pinned to `timeline`
      // without one, which is not a choice the user made and not something to send.
      timelineMode: this.hasTimelineSwitch() ? timelineMode : null,
      startDate, endDate,
      emitsTime: this.emitsTime(),
      usesTimeframe: timeframe,
      startTime: this.resolvedStartTime(),
      endTime: this.resolvedEndTime(),
      excludeWeekends,
    });

    const period: Period = {mode, timelineMode, startDate, endDate, params};
    if (timeframe) {
      period.startTime = this.resolvedStartTime();
      period.endTime = this.resolvedEndTime();
      if (excludeWeekends) period.excludeWeekends = true;
    }
    return period;
  });

  private readonly validPeriod = computed(() => this.periodInvalid() ? null : this.period());

  /** The instances the date models were built with — reference equality marks one still pristine. */
  private readonly defaultStartDate = this.startDate();
  private readonly defaultEndDate = this.endDate();

  /**
   * The `startDate`/`endDate` model defaults evaluate before the inputs exist, so they are always
   * on the browser clock. Once a zone is known ({@link effectiveTimezone}), this replaces a model
   * still holding its original default instance; one the consumer bound or the user edited holds another reference
   * and is left alone. The models are read via `untracked` so an edit never re-triggers the
   * correction. Declared above `periodChange`: effects flush in creation order, so the corrected
   * dates land inside the first `toObservable` emission — the one `skip(1)` swallows — and no
   * spurious initial `periodChange` fires (spec-pinned).
   */
  private readonly correctDefaultsToTimezone = effect(() => {
    const timezone = this.effectiveTimezone();
    if (!timezone) {
      return;
    }
    untracked(() => {
      if (this.startDate() === this.defaultStartDate) {
        this.startDate.set(nowInTimezone(timezone).startOf('day').subtract(1, 'week'));
      }
      if (this.endDate() === this.defaultEndDate) {
        this.endDate.set(nowInTimezone(timezone).endOf('day'));
      }
    });
  });

  /**
   * Emits the resolved selection. Debounced so holding the stepper fires one request, and
   * deduped by value: `period()` clones fresh Moments on every recomputation, so a consumer
   * that binds a model one-way and copies the emitted value back in (rather than binding
   * `[(endDate)]` etc.) feeds back a same-instant-but-different-reference date. Without this,
   * that echo reads as a "change" and re-emits forever.
   */
  readonly periodChange = outputFromObservable(toObservable(this.validPeriod).pipe(
    skip(1), // the initial state is not a change
    debounceTime(250),
    // Filtered before comparing: the comparator assumes real periods, and `validPeriod` is
    // `null` whenever the selection is invalid, which would otherwise throw mid-stream.
    filter((period): period is Period => period !== null),
    distinctUntilChanged(periodsEqual),
  ));

  /**
   * Clicking a field selects all of it, so typing replaces the value instead of inserting
   * into it. Guarded because `select()` throws on input types that have no text selection.
   */
  protected selectAll(event: Event): void {
    const input = event.target as HTMLInputElement;
    // A read-only (pick-only) date field is a trigger, not a text box — nothing to select.
    if (input.readOnly) {
      return;
    }
    try {
      input.select();
    } catch {
      // Native date/time controls — they handle their own segment selection.
    }
  }

  /**
   * Enter finishes editing, exactly as clicking away does: blurring drops the draft, so the
   * field snaps to the canonical formatting of whatever the typing already committed.
   */
  protected commitOnEnter(event: Event): void {
    // Finishing a field must not submit a form the selector happens to sit in.
    event.preventDefault();
    this.openPanel.set(null);
    (event.target as HTMLInputElement).blur();
  }

  protected togglePanel(panel: Exclude<OpenPanel, null>): void {
    this.openPanel.update(open => open === panel ? null : panel);
  }

  protected selectMode(mode: PeriodMode): void {
    this.openPanel.set(null);
    this.mode.set(mode);
    if (mode === 'recent') {
      this.syncRecentDates();
    }
  }

  protected setTimelineMode(timelineMode: PeriodTimelineMode): void {
    this.timelineMode.set(timelineMode);
    // Switching to Schedule can invalidate 'hours' (see `effectiveRecentUnits`). Falling back
    // is not enough on its own: it only relabels the unit, leaving `startDate`/`endDate` as
    // whatever the excluded unit last computed — stale "last 2 hours" moments mislabelled
    // "2 days" once clipped to whole days. Re-syncing recomputes them for the unit actually
    // in effect now.
    if (this.effectiveMode() === 'recent' && !this.effectiveRecentUnits().includes(this.recentUnit())) {
      this.recentUnit.set(this.effectiveRecentUnits()[0]);
      this.syncRecentDates();
    }
  }

  protected pickStartDate(date: Moment): void {
    this.openPanel.set(null);
    this.startDateField.reset();
    this.applyStartDate(date);
  }

  protected pickEndDate(date: Moment): void {
    this.openPanel.set(null);
    this.endDateField.reset();
    this.applyEndDate(date);
  }

  protected pickDay(date: Moment): void {
    this.openPanel.set(null);
    this.dayField.reset();
    this.setDay(date);
  }

  protected stepDay(days: number): void {
    this.setDay(this.endDate().clone().add(days, 'days'));
  }

  protected stepRecent(delta: number): void {
    this.setRecentValue(this.recentValue() + delta);
  }

  /**
   * Snaps the field back to the model. `[ngModel]` is one-way, so it only rewrites the DOM
   * when the bound value changes — typing `0` while the value is already the minimum clamps
   * to the same number and would otherwise leave the rejected `0` on screen.
   */
  protected syncRecentInput(input: HTMLInputElement): void {
    input.value = String(this.recentValue());
  }

  protected setRecentValue(value: number): void {
    const min = this.effectiveMinRecent();
    const clamped = Math.min(Math.max(Math.round(value) || min, min), this.maxRecent());
    this.recentValue.set(clamped);
    this.syncRecentDates();
  }

  protected selectRecentUnit(unit: RecentUnit): void {
    this.openPanel.set(null);
    this.recentUnit.set(unit);
    this.syncRecentDates();
  }

  protected toggleWeekends(): void {
    this.weekends.update(weekends => !weekends);
  }

  /**
   * Builds a field that keeps the raw text while it is being edited and only touches the model
   * once editing finishes. Typing merely validates, so nothing is emitted mid-word and the
   * value the consumer sees always matches the formatting on screen.
   */
  private textField(
    source: Signal<string>,
    parse: (text: string) => Moment | null,
    apply: (value: Moment) => void,
    // Given for a field that may be emptied back to unset; without it, an empty field reverts.
    clear?: () => void,
  ): TextField {
    const draft = signal<string | null>(null);
    const invalid = signal(false);
    const isEmpty = (text: string) => !!clear && text.trim() === '';
    return {
      value: computed(() => draft() ?? source()),
      invalid,
      editing: computed(() => draft() !== null),
      type: (text: string) => {
        draft.set(text);
        // Emptying a clearable field is a valid intent, not a parse failure.
        invalid.set(!isEmpty(text) && parse(text) === null);
      },
      finish: () => {
        const text = draft();
        draft.set(null);
        invalid.set(false);
        if (text === null) return;
        // Cleared → unset; parseable → commit; anything else is dropped, reverting the field.
        if (isEmpty(text)) clear!();
        else { const parsed = parse(text); if (parsed) apply(parsed); }
      },
      reset: () => {
        draft.set(null);
        invalid.set(false);
      },
    };
  }

  /** A committed endpoint time from its picker: `HH:mm`, or `null` once it is emptied. */
  protected setStartTime(time: string | null): void {
    if (time === null) {
      this.clearStartTime();
    } else {
      this.applyStartTime(time);
    }
  }

  protected setEndTime(time: string | null): void {
    if (time === null) {
      this.clearEndTime();
    } else {
      this.applyEndTime(time);
    }
  }

  private parseDate(text: string): Moment | null {
    const formats = this.formats.profile() === 'us' ? [...US_DATE_INPUT_FORMATS, ...DATE_INPUT_FORMATS] : DATE_INPUT_FORMATS;
    const parsed = moment(text.trim(), formats, true);
    if (!parsed.isValid()) return null;
    // A year-less US date (`Sep 16`, `9/16`) is in the current year — the one the field leaves out —
    // taken in the company's zone, as the display does, rather than on the browser clock.
    if (!String(parsed.creationData().format).includes('Y')) {
      parsed.year(nowInTimezone(this.formats.timezone()).year());
    }
    // A typed date has to respect the same bounds the calendar and the day stepper enforce,
    // otherwise typing is the one way around them.
    const min = this.minDate();
    const max = this.maxDate();
    if (min && parsed.isBefore(min, 'day')) return null;
    return max && parsed.isAfter(max, 'day') ? null : parsed;
  }

  private applyStartTime(time: string): void {
    if (this.sharedTimeframe()) {
      this.startTime.set(time);
      return;
    }
    const [hours, minutes] = time.split(':').map(Number);
    this.startTimeSet.set(true);
    this.startDate.update(current => current.clone().hours(hours).minutes(minutes).seconds(0).milliseconds(0));
  }

  private applyEndTime(time: string): void {
    if (this.sharedTimeframe()) {
      this.endTime.set(time);
      return;
    }
    const [hours, minutes] = time.split(':').map(Number);
    this.endTimeSet.set(true);
    this.endDate.update(current => current.clone().hours(hours).minutes(minutes).seconds(0).milliseconds(0));
  }

  /** Emptying an endpoint time unsets it: back to the greyed whole-day default. */
  private clearStartTime(): void {
    if (this.sharedTimeframe()) {
      this.startTime.set(null);
      return;
    }
    this.startTimeSet.set(false);
    this.startDate.update(current => current.clone().startOf('day'));
  }

  private clearEndTime(): void {
    if (this.sharedTimeframe()) {
      this.endTime.set(null);
      return;
    }
    this.endTimeSet.set(false);
    this.endDate.update(current => current.clone().endOf('day'));
  }

  /**
   * Keeps whatever time this endpoint already carries — typed, or supplied by the consumer —
   * when only the date changes. `hasStartTime`/`hasEndTime` is the same "meaningful time" check
   * that decides whether the time field shows a value or its placeholder, so a consumer-provided
   * `08:00` survives a new date pick exactly as a user-typed one would; only the untouched
   * `00:00`/`23:59` placeholder resets per date.
   */
  private applyStartDate(date: Moment): void {
    const preserveTime = this.hasStartTime();
    this.startDate.update(current => date.clone()
      .hours(preserveTime ? current.hours() : 0)
      .minutes(preserveTime ? current.minutes() : 0)
      .seconds(0).milliseconds(0));
  }

  private applyEndDate(date: Moment): void {
    this.endDate.update(current => this.hasEndTime()
      ? date.clone().hours(current.hours()).minutes(current.minutes()).seconds(0).milliseconds(0)
      : date.clone().endOf('day'));
  }

  private labelFor(mode: PeriodMode): string {
    return this.modeLabels()[mode] ?? MODE_LABELS[mode];
  }

  /** `day` mode keeps its selection in `endDate`; both endpoints are derived from it. */
  private setDay(day: Moment): void {
    this.endDate.set(day.clone().endOf('day'));
    this.endTimeSet.set(false);
  }

  private syncRecentDates(): void {
    const end = nowInTimezone(this.effectiveTimezone());
    this.startDate.set(end.clone()
      .subtract(this.recentValue(), this.effectiveRecentUnit() as unitOfTime.DurationConstructor));
    this.endDate.set(end);
  }
}
